/**
 * sl-jsx3.16.4: Group Members Editor
 *
 * Inline editor for managing group members inside the expanded group row.
 * Auto-sync groups are read-only.
 */
import {
  IconButton,
  Input,
  Loading,
  notify,
  useConfirmModal,
} from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { PersonRow } from '@affine/core/components/identity-row';
import { WorkspaceServerService } from '@affine/core/modules/cloud';
import {
  GroupApiService,
  type GroupData,
  type GroupMemberData,
} from '@affine/core/modules/group-api';
import {
  type Member,
  MemberSearchService,
} from '@affine/core/modules/permissions';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { UserFriendlyError } from '@affine/error';
import { getMembersByWorkspaceIdQuery } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { DeleteIcon, PlusIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { WorkspaceRoleBadge } from '../shared/workspace-role-badge';
import * as styles from './styles.css';

interface GroupMembersEditorProps {
  group: GroupData;
}

export const GroupMembersEditor = ({ group }: GroupMembersEditorProps) => {
  const t = useI18n();
  const groupApiService = useService(GroupApiService);
  const serverService = useService(WorkspaceServerService);
  const memberSearchService = useService(MemberSearchService);
  const memberSearchSession = useMemo(
    () => memberSearchService.createSession(),
    [memberSearchService]
  );
  useEffect(() => {
    return () => {
      memberSearchSession.dispose();
    };
  }, [memberSearchSession]);
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;

  // Group members state
  const [groupMembers, setGroupMembers] = useState<GroupMemberData[]>([]);
  const [allWorkspaceMembers, setAllWorkspaceMembers] = useState<Member[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [searchText, setSearchText] = useState('');

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    const server = serverService.server;
    if (!server) {
      setGroupMembers([]);
      setAllWorkspaceMembers([]);
      setIsLoading(false);
      return;
    }
    try {
      const [members, wsData] = await Promise.all([
        groupApiService.getGroupMembers(workspaceId, group.id),
        server.gql({
          query: getMembersByWorkspaceIdQuery,
          variables: { workspaceId, skip: 0, take: 200 },
        }),
      ]);
      setGroupMembers(members);
      setAllWorkspaceMembers(wsData.workspace.members);
    } catch (err) {
      notify.error({ title: UserFriendlyError.fromAny(err).message });
    } finally {
      setIsLoading(false);
    }
  }, [groupApiService, group.id, serverService.server, workspaceId]);

  useEffect(() => {
    void fetchData();
    setSearchText('');
  }, [fetchData]);

  // Build userId → Member map
  const memberMap = useMemo(() => {
    const map = new Map<string, Member>();
    for (const m of allWorkspaceMembers) {
      map.set(m.id, m);
    }
    return map;
  }, [allWorkspaceMembers]);

  // Group member userIds set for quick lookup
  const groupMemberIds = useMemo(
    () => new Set(groupMembers.map(gm => gm.userId)),
    [groupMembers]
  );

  // Search-based member adding
  const searchResults = useLiveData(memberSearchSession.result$);
  const isSearching = useLiveData(memberSearchSession.isLoading$);

  const debouncedSearch = useMemo(
    () => debounce((value: string) => memberSearchSession.search(value), 300),
    [memberSearchSession]
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  useEffect(() => {
    const query = searchText.trim();
    if (query) {
      debouncedSearch(query);
    } else {
      debouncedSearch.cancel();
      memberSearchSession.reset();
    }
  }, [debouncedSearch, memberSearchSession, searchText]);

  const visibleMembers = useMemo(() => {
    const query = searchText.trim().toLocaleLowerCase();
    if (!query) {
      return groupMembers
        .map(groupMember => memberMap.get(groupMember.userId))
        .filter((member): member is Member => Boolean(member));
    }

    const byId = new Map<string, Member>();
    for (const member of allWorkspaceMembers) {
      const searchable = `${member.name ?? ''} ${member.email ?? ''}`
        .trim()
        .toLocaleLowerCase();
      if (searchable.includes(query)) {
        byId.set(member.id, member);
      }
    }
    for (const member of searchResults) {
      byId.set(member.id, member);
    }
    return [...byId.values()];
  }, [allWorkspaceMembers, groupMembers, memberMap, searchResults, searchText]);

  const handleAddMember = useAsyncCallback(
    async (userId: string) => {
      try {
        await groupApiService.addGroupMember(workspaceId, group.id, userId);
        notify.success({
          title:
            t['com.streamlain.settings.workspace.groups.members.add.success'](),
        });
        await fetchData();
      } catch (err) {
        notify.error({ title: UserFriendlyError.fromAny(err).message });
      }
    },
    [fetchData, group.id, groupApiService, t, workspaceId]
  );

  const { openConfirmModal } = useConfirmModal();

  const handleRemoveMember = useCallback(
    (userId: string) => {
      const member = memberMap.get(userId);
      const name = member?.name || member?.email || userId;
      openConfirmModal({
        title:
          t[
            'com.streamlain.settings.workspace.groups.members.remove.confirm.title'
          ](),
        description: t[
          'com.streamlain.settings.workspace.groups.members.remove.confirm.description'
        ]({ name }),
        confirmText:
          t[
            'com.streamlain.settings.workspace.groups.members.remove.confirm.confirm'
          ](),
        cancelText: t['Cancel'](),
        confirmButtonOptions: { variant: 'error' },
        onConfirm: async () => {
          try {
            await groupApiService.removeGroupMember(
              workspaceId,
              group.id,
              userId
            );
            notify.success({
              title:
                t[
                  'com.streamlain.settings.workspace.groups.members.remove.success'
                ](),
            });
            await fetchData();
          } catch (err) {
            notify.error({ title: UserFriendlyError.fromAny(err).message });
          }
        },
      });
    },
    [
      fetchData,
      group.id,
      groupApiService,
      memberMap,
      openConfirmModal,
      t,
      workspaceId,
    ]
  );

  return (
    <section className={styles.inlineMembersEditor}>
      <div className={styles.inlineMembersHeader}>
        <div>
          <div className={styles.inlineMembersTitle}>
            {t['com.streamlain.settings.workspace.groups.members.title']()} (
            {groupMembers.length})
          </div>
          <div className={styles.inlineMembersDescription}>
            {group.isAutoSync
              ? (t[
                  'com.streamlain.settings.workspace.groups.auto-sync.description'
                ]?.() ?? 'Members are managed automatically for this group.')
              : (t[
                  'com.streamlain.settings.workspace.groups.members.search.placeholder'
                ]?.() ?? 'Search workspace members')}
          </div>
        </div>
      </div>

      {!group.isAutoSync ? (
        <Input
          data-testid="group-members-search-input"
          placeholder={t[
            'com.streamlain.settings.workspace.groups.members.search.placeholder'
          ]()}
          value={searchText}
          onChange={setSearchText}
        />
      ) : null}

      {isLoading ||
      (searchText.trim() && isSearching && visibleMembers.length === 0) ? (
        <div className={styles.loadingContainer}>
          <Loading size={16} />
        </div>
      ) : (
        <div className={styles.groupsPanel}>
          {visibleMembers.length === 0 ? (
            <div className={styles.emptyState}>
              <span>
                {searchText.trim()
                  ? t[
                      'com.streamlain.settings.workspace.groups.members.search.empty'
                    ]()
                  : t[
                      'com.streamlain.settings.workspace.groups.members.empty'
                    ]()}
              </span>
            </div>
          ) : (
            visibleMembers.map(member => {
              const inGroup = groupMemberIds.has(member.id);
              return (
                <PersonRow
                  key={member.id}
                  dataTestId={
                    searchText.trim()
                      ? `group-member-search-result-${member.id}`
                      : `group-member-row-${member.id}`
                  }
                  className={styles.groupMemberRow}
                  person={member}
                  fallbackName={member.id}
                  avatarSize={32}
                  badges={
                    <>
                      <WorkspaceRoleBadge role={member.permission} />
                      {inGroup ? (
                        <span className={styles.badge}>
                          {t[
                            'com.streamlain.settings.workspace.groups.members.title'
                          ]?.() ?? 'Member'}
                        </span>
                      ) : null}
                    </>
                  }
                  actions={
                    group.isAutoSync ? undefined : inGroup ? (
                      <IconButton
                        data-testid={`group-member-remove-${member.id}`}
                        onClick={() => handleRemoveMember(member.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    ) : (
                      <IconButton
                        data-testid={`group-member-add-${member.id}`}
                        onClick={() => handleAddMember(member.id)}
                      >
                        <PlusIcon />
                      </IconButton>
                    )
                  }
                />
              );
            })
          )}
        </div>
      )}
    </section>
  );
};
